/**
 * ACR Clinical Decision Support panel.
 *
 * Layout (mirrors AttachedDocument):
 *   ┌──────────────────────────┬──────────────────────────┐
 *   │ Clinical inputs          │ ACR Decision Output      │
 *   │ ─ 4 tabs (REFERRAL/...)  │ (recommended exam, etc.) │
 *   │ ─ Free-text + uploads    ├──────────────────────────┤
 *   │ ─ Run ACR Decision       │ ACR Q&A chat             │
 *   └──────────────────────────┴──────────────────────────┘
 *
 * Backend contract: see ACR_AnalyzeReferral_Integration_Exact.md.
 *
 * Key client behavior:
 *  - Each of the four categories (REFERRAL, PRIORS, LABS, DIAGNOSES) keeps
 *    its own free-text buffer and its own list of uploaded files.
 *  - Run ACR Decision uploads any pending files in one combined request,
 *    then calls AnalyzeReferral with the returned fileId; otherwise it
 *    sends the four free-text blocks combined into a labeled referralText.
 *  - The Q&A pane is enabled once an ACR decision exists and includes
 *    the selected rule + explanation in every follow-up request so the
 *    backend can reason about the recommendation.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  FiFileText,
  FiLoader,
  FiSend,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import { FaNotesMedical } from "react-icons/fa";
import {
  acrAskFollowup,
  analyzeAcrReferral,
  uploadAcrReferralFilesParallel,
} from "../../api/acrApi";
import {
  resolveCreFeatureNotAllowedMessage,
} from "../../cre/crePermissions";

const CATEGORY = {
  REFERRAL: "referral",
  PRIORS: "priors",
  LABS: "labs",
  DIAGNOSES: "diagnoses",
};

const CATEGORY_ORDER = [
  CATEGORY.REFERRAL,
  CATEGORY.PRIORS,
  CATEGORY.LABS,
  CATEGORY.DIAGNOSES,
];

const MODEL_V1 = "medilis.ai.V1(faster)";
const MODEL_V2 = "medilis.ai.V2(deeper)";
const MODEL_V3 = "medilis.ai.V3(thinking)";
const ACCEPTED_FILE_EXT = ".jpg,.jpeg,.png,.gif,.bmp,.pdf";
const MAX_FILE_MB = 30;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

function emptyByCategory(initial = "") {
  return {
    [CATEGORY.REFERRAL]: initial,
    [CATEGORY.PRIORS]: initial,
    [CATEGORY.LABS]: initial,
    [CATEGORY.DIAGNOSES]: initial,
  };
}

function emptyFilesByCategory() {
  return {
    [CATEGORY.REFERRAL]: [],
    [CATEGORY.PRIORS]: [],
    [CATEGORY.LABS]: [],
    [CATEGORY.DIAGNOSES]: [],
  };
}

/**
 * Build the {referral, priors, labs, diagnoses} categorizedInputs object
 * the backend now accepts directly. Empty categories are omitted so the
 * backend doesn't see noise.
 */
function buildCategorizedInputs(texts) {
  const out = {};
  for (const key of CATEGORY_ORDER) {
    const value = (texts[key] || "").trim();
    if (value) out[key] = value;
  }
  return out;
}

function flattenFiles(filesByCategory) {
  return CATEGORY_ORDER.flatMap((key) =>
    (filesByCategory[key] || []).map((f) => ({ category: key, file: f }))
  );
}

/**
 * Pick the value from a server payload that may use camelCase or PascalCase.
 */
function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

export default function AcrDecisionSupport({ language }) {
  const { t } = useTranslation("acr");

  const [activeCategory, setActiveCategory] = useState(CATEGORY.REFERRAL);
  const [texts, setTexts] = useState(() => emptyByCategory(""));
  const [files, setFiles] = useState(() => emptyFilesByCategory());
  const [model, setModel] = useState(MODEL_V2);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);

  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);

  // Auto-scroll the Q&A panel to the latest message whenever a new turn
  // is appended or while the assistant is thinking, so the user always
  // sees the most recent question/answer.
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    // Defer to the next frame so layout has settled (chat bubble height
    // computed, "thinking" row mounted) before we snap to the bottom.
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [chatMessages, chatSending]);

  const labelOfCategory = useCallback(
    (key) => {
      switch (key) {
        case CATEGORY.REFERRAL:
          return t("categoryReferral");
        case CATEGORY.PRIORS:
          return t("categoryPriors");
        case CATEGORY.LABS:
          return t("categoryLabs");
        case CATEGORY.DIAGNOSES:
          return t("categoryDiagnoses");
        default:
          return key;
      }
    },
    [t]
  );

  const helperOfCategory = useCallback(
    (key) => {
      switch (key) {
        case CATEGORY.REFERRAL:
          return t("categoryReferralFull");
        case CATEGORY.PRIORS:
          return t("categoryPriorsFull");
        case CATEGORY.LABS:
          return t("categoryLabsFull");
        case CATEGORY.DIAGNOSES:
          return t("categoryDiagnosesFull");
        default:
          return "";
      }
    },
    [t]
  );

  const totalFilesCount = useMemo(
    () =>
      CATEGORY_ORDER.reduce(
        (sum, key) => sum + (files[key]?.length || 0),
        0
      ),
    [files]
  );

  const totalTextChars = useMemo(
    () =>
      CATEGORY_ORDER.reduce(
        (sum, key) => sum + ((texts[key] || "").trim().length || 0),
        0
      ),
    [texts]
  );

  const hasAnyInput = totalFilesCount > 0 || totalTextChars > 0;

  const handleSelectFiles = useCallback(
    (fileList) => {
      const incoming = fileList ? Array.from(fileList) : [];
      if (!incoming.length) return;

      const tooBig = incoming.find((f) => f.size > MAX_FILE_BYTES);
      if (tooBig) {
        toast.error(t("toastFileTooBig", { max: MAX_FILE_MB }));
        return;
      }

      setFiles((prev) => ({
        ...prev,
        [activeCategory]: [...(prev[activeCategory] || []), ...incoming],
      }));
    },
    [activeCategory, t]
  );

  const handleFileInputChange = useCallback(
    (e) => {
      handleSelectFiles(e.target.files);
      e.target.value = "";
    },
    [handleSelectFiles]
  );

  const handleRemoveFile = useCallback((category, idx) => {
    setFiles((prev) => ({
      ...prev,
      [category]: (prev[category] || []).filter((_, i) => i !== idx),
    }));
  }, []);

  const resetAll = useCallback(() => {
    setTexts(emptyByCategory(""));
    setFiles(emptyFilesByCategory());
    setResult(null);
    setError(null);
    setChatMessages([]);
    setChatInput("");
  }, []);

  /** Run the ACR pipeline based on the current inputs. */
  const runAcrDecision = useCallback(async () => {
    setError(null);
    if (!hasAnyInput) {
      toast.error(t("pleaseProvideInput"));
      return;
    }

    setRunning(true);
    setResult(null);
    setChatMessages([]);
    try {
      const allFiles = flattenFiles(files).map((entry) => entry.file);
      const categorizedInputs = buildCategorizedInputs(texts);

      const analyzeBody = { model, language };
      if (Object.keys(categorizedInputs).length) {
        analyzeBody.categorizedInputs = categorizedInputs;
      }
      if (allFiles.length > 0) {
        // Upload one file per call (per backend spec) and pass the
        // collected FileIds together as `fileIds` to AnalyzeReferral.
        const fileIds = await uploadAcrReferralFilesParallel(allFiles);
        if (!fileIds.length) {
          throw new Error(t("toastUploadFailed"));
        }
        analyzeBody.fileIds = fileIds;
      }

      const payload = await analyzeAcrReferral(analyzeBody);
      setResult(payload);

      const topicFound = pick(payload, "acrTopicFound", "AcrTopicFound");
      if (topicFound === false) {
        toast.warning(t("topicNotFound"));
      }
    } catch (err) {
      const msg =
        resolveCreFeatureNotAllowedMessage(err) ||
        err?.message ||
        t("toastAnalysisFailed");
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  }, [files, texts, model, language, hasAnyInput, t]);

  /** Send a follow-up question to the Q&A endpoint. */
  const sendChatMessage = useCallback(
    async (questionRaw) => {
      const question = String(questionRaw || "").trim();
      if (!question || chatSending) return;
      const selectedRule = pick(result, "selectedRule", "SelectedRule");
      if (!selectedRule) {
        toast.info(t("qaNeedsDecisionFirst"));
        return;
      }
      const userMsg = { role: "user", content: question };
      const priorConversation = chatMessages.slice();
      setChatMessages((prev) => [...prev, userMsg]);
      setChatInput("");
      setChatSending(true);

      try {
        const { answer } = await acrAskFollowup({
          question,
          selectedRule,
          explanation: pick(result, "explanation", "Explanation") || "",
          detectedAcrTopic: pick(result, "detectedAcrTopic", "DetectedAcrTopic"),
          conversationHistory: priorConversation,
          language,
          model,
        });
        const text = (answer || "").trim();
        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: text || t("qaEmptyAnswer"),
          },
        ]);
      } catch (err) {
        const msg =
          resolveCreFeatureNotAllowedMessage(err) ||
          err?.message ||
          t("toastQaFailed");
        setChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: msg, isError: true },
        ]);
        toast.error(msg);
      } finally {
        setChatSending(false);
      }
    },
    [chatMessages, chatSending, result, language, model, t]
  );

  const onChatSubmit = useCallback(
    (e) => {
      e?.preventDefault?.();
      sendChatMessage(chatInput);
    },
    [chatInput, sendChatMessage]
  );

  const isRtlLang = language === "he" || language === "ar";

  /* ---------------------------- DERIVED RESULT ---------------------------- */
  const detectedTopic = pick(result, "detectedAcrTopic", "DetectedAcrTopic");
  const topicFound = pick(result, "acrTopicFound", "AcrTopicFound");
  const explanation = pick(result, "explanation", "Explanation");
  const selectedRule = pick(result, "selectedRule", "SelectedRule");
  const matchingRules = pick(result, "matchingRules", "MatchingRules") || [];
  const extractedText = pick(result, "extractedText", "ExtractedText");
  // Q&A is available only when the AI matched the referral to an ACR rule.
  const qaAvailable = !!selectedRule && topicFound !== false;

  /* -------------------------------- RENDER -------------------------------- */
  return (
    <div className="mt-6 dark:bg-gray-900">
      <div className="flex flex-col xl:flex-row xl:h-[80vh]">
        {/* ============================ LEFT PANE ============================ */}
        <div className="xl:w-1/2 min-h-0 p-4 bg-white dark:bg-gray-800 border dark:border-gray-700 xl:rounded-tl-3xl xl:rounded-bl-3xl rounded-tr-3xl rounded-tl-3xl xl:rounded-tr-none overflow-y-auto shadow-sm dark:shadow-gray-900">
          <div className="flex items-center gap-2 mb-4">
            <FaNotesMedical
              className="text-[#A4C639] dark:text-[#CBEA7B]"
              size={20}
            />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t("inputsTitle")}
            </h2>
          </div>

          {/* Category tabs */}
          <div className="mb-3 overflow-x-auto">
            <div className="inline-flex min-w-full sm:min-w-0 rounded-xl bg-gray-100 dark:bg-gray-700 p-1 gap-1">
              {CATEGORY_ORDER.map((key) => {
                const count =
                  ((texts[key] || "").trim() ? 1 : 0) +
                  (files[key]?.length || 0);
                const active = activeCategory === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveCategory(key)}
                    className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors ${
                      active
                        ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow"
                        : "text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-600/60"
                    }`}
                  >
                    {labelOfCategory(key).toUpperCase()}
                    {count > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#A4C639]/30 dark:bg-[#CBEA7B]/30 text-[10px] font-semibold text-gray-800 dark:text-gray-100">
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {helperOfCategory(activeCategory)}
          </p>

          {/* Free-text input for the active category */}
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
              <FiFileText />
              {t("pastedTextSection")}
            </label>
            <textarea
              value={texts[activeCategory] || ""}
              onChange={(e) =>
                setTexts((prev) => ({
                  ...prev,
                  [activeCategory]: e.target.value,
                }))
              }
              placeholder={t("pasteTextLabel")}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-y focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B] focus:border-transparent transition-all text-sm"
              rows={5}
              dir={isRtlLang ? "rtl" : "ltr"}
              disabled={running}
            />
          </div>

          {/* Attachments for the active category */}
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between mb-2 gap-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">
                <FiUpload />
                {t("attachmentsSection")}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_EXT}
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={running}
                className="text-xs px-2.5 py-1 rounded-md bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                <FiUpload className="w-3.5 h-3.5" />
                {t("uploadFile")}
              </button>
            </div>
            {(files[activeCategory] || []).length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                {t("noFiles")}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {(files[activeCategory] || []).map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}-${f.lastModified || 0}`}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                  >
                    <span className="text-xs text-gray-700 dark:text-gray-200 truncate">
                      {f.name}{" "}
                      <span className="text-gray-400">
                        ({Math.round((f.size || 0) / 1024)} KB)
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(activeCategory, idx)}
                      title={t("removeFile")}
                      className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
              {t("acceptedFiles", { max: MAX_FILE_MB })}
            </p>
          </div>

          {/* Model picker + Run button */}
          <div className="mb-2 flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                {t("model")}
              </label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={running}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              >
                <option value={MODEL_V1}>{t("modelV1")}</option>
                <option value={MODEL_V2}>{t("modelV2")}</option>
                <option value={MODEL_V3}>{t("modelV3")}</option>
              </select>
            </div>
            <button
              type="button"
              onClick={runAcrDecision}
              disabled={running || !hasAnyInput}
              className="px-4 py-2 rounded-lg xl:rounded-full bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[40px]"
            >
              {running ? (
                <>
                  <FiLoader className="animate-spin" />
                  {t("running")}
                </>
              ) : (
                <>
                  <FaNotesMedical />
                  {t("runDecision")}
                </>
              )}
            </button>
          </div>
          {hasAnyInput && (
            <button
              type="button"
              onClick={resetAll}
              disabled={running}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
            >
              {t("clearAll")}
            </button>
          )}

          {error && (
            <div className="mt-3 p-3 rounded bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* ============================ RIGHT PANE =========================== */}
        <div className="xl:w-1/2 min-h-0 border dark:border-gray-700 xl:rounded-tr-3xl xl:rounded-br-3xl rounded-bl-3xl rounded-br-3xl xl:rounded-bl-none bg-white dark:bg-gray-800 flex flex-col">
          {/* ----- TOP: Decision Output ----- */}
          <div className="flex-1 min-h-0 p-4 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <FaNotesMedical
                className="text-[#A4C639] dark:text-[#CBEA7B]"
                size={18}
              />
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t("decisionOutputTitle")}
              </h3>
            </div>

            {!result && !running && (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-6">
                {t("noDecisionYet")}
              </p>
            )}
            {running && !result && (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-6">
                <FiLoader className="animate-spin" />
                {t("running")}
              </div>
            )}

            {result && (
              <AcrDecisionView
                result={result}
                detectedTopic={detectedTopic}
                topicFound={topicFound}
                explanation={explanation}
                selectedRule={selectedRule}
                matchingRules={matchingRules}
                extractedText={extractedText}
                language={language}
                t={t}
              />
            )}
          </div>

          {/* ----- BOTTOM: Q&A chat ----- */}
          <div className="border-t dark:border-gray-700 flex flex-col h-[40vh] xl:h-[45%] min-h-[260px]">
            <div className="flex items-center gap-2 px-4 py-2 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t("qaTitle")}
              </h3>
            </div>

            <div
              ref={chatScrollRef}
              className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3"
            >
              {chatMessages.length === 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <p className="mb-2">
                    {!result
                      ? t("qaNeedsDecisionFirst")
                      : !qaAvailable
                      ? t("qaUnavailableNoRule")
                      : t("qaEmpty")}
                  </p>
                  {qaAvailable && (
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        t("qaSampleWhyNotMri"),
                        t("qaSampleAlternative"),
                        t("qaSampleContrast"),
                        t("qaSampleMissingInfo"),
                      ].map((q) => (
                        <button
                          key={q}
                          type="button"
                          onClick={() => sendChatMessage(q)}
                          disabled={chatSending}
                          className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {chatMessages.map((m, i) => (
                <ChatBubble key={i} message={m} isRtl={isRtlLang} />
              ))}
              {chatSending && (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <FiLoader className="animate-spin" />
                  {t("qaThinking")}
                </div>
              )}
            </div>

            <form
              onSubmit={onChatSubmit}
              className="p-3 border-t dark:border-gray-700 flex items-center gap-2"
            >
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={t("qaPlaceholder")}
                disabled={!qaAvailable || chatSending}
                dir={isRtlLang ? "rtl" : "ltr"}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]"
              />
              <button
                type="submit"
                disabled={!qaAvailable || chatSending || !chatInput.trim()}
                className="px-3 py-2 rounded-lg bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                title={t("qaSend")}
              >
                <FiSend className="w-4 h-4" />
                <span className="hidden sm:inline">{t("qaSend")}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Subcomponents ------------------------------- */

function ChatBubble({ message, isRtl }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        dir={isRtl ? "rtl" : "ltr"}
        className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
          isUser
            ? "bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black rounded-br-sm"
            : message.isError
            ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-bl-sm"
            : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-sm"
        }`}
      >
        {message.content}
      </div>
    </div>
  );
}

function AcrDecisionView({
  detectedTopic,
  topicFound,
  explanation,
  selectedRule,
  matchingRules,
  extractedText,
  language,
  t,
}) {
  const [showExtracted, setShowExtracted] = useState(false);
  const [showMatches, setShowMatches] = useState(false);

  const isRtlLang = language === "he" || language === "ar";
  const topicMissing = topicFound === false;

  return (
    <div className="space-y-3">
      {/* Topic chip */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {t("topicLabel")}:
        </span>
        <span
          className={
            topicMissing
              ? "px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs"
              : "px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 text-xs"
          }
        >
          {detectedTopic || "—"}
        </span>
      </div>

      {topicMissing && (
        <div className="p-3 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
          {t("topicNotFoundHint")}
        </div>
      )}

      {/* Selected rule */}
      {selectedRule && (
        <div className="rounded-lg border border-blue-300 dark:border-blue-500 bg-blue-50/60 dark:bg-blue-900/20 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wide text-blue-800 dark:text-blue-200">
              {t("recommended")}
            </span>
            <RuleField
              label={t("ruleId")}
              value={pick(selectedRule, "ruleId", "RuleId")}
              compact
            />
          </div>
          <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {pick(selectedRule, "recommendedExam", "RecommendedExam") || "—"}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <RuleField
              label={t("recommendedExamRating")}
              value={pick(
                selectedRule,
                "recommendedExamRating",
                "RecommendedExamRating"
              )}
              hint={pick(
                selectedRule,
                "recommendedExamScore",
                "RecommendedExamScore"
              )}
            />
            <RuleField
              label={t("requestedExam")}
              value={pick(selectedRule, "requestedExam", "RequestedExam")}
            />
            <RuleField
              label={t("requestedExamRating")}
              value={pick(
                selectedRule,
                "requestedExamRating",
                "RequestedExamRating"
              )}
              hint={pick(
                selectedRule,
                "requestedExamScore",
                "RequestedExamScore"
              )}
            />
            <RuleField
              label={t("alternativeExam")}
              value={pick(selectedRule, "alternativeExam", "AlternativeExam")}
            />
            <RuleField
              label={t("contrastMaterial")}
              value={pick(selectedRule, "contrastMaterial", "ContrastMaterial")}
            />
            <RuleField
              label={t("systemDecision")}
              value={pick(selectedRule, "systemDecision", "SystemDecision")}
            />
            <RuleField
              label={t("variantLabel")}
              value={pick(selectedRule, "acrVariant", "AcrVariant")}
              wide
            />
          </div>
        </div>
      )}

      {/* Explanation */}
      {explanation && (
        <div>
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
            {t("explanation")}
          </p>
          <p
            dir={isRtlLang ? "rtl" : "ltr"}
            className="text-sm leading-relaxed whitespace-pre-wrap text-gray-700 dark:text-gray-300"
          >
            {explanation}
          </p>
        </div>
      )}

      {/* Matching rules */}
      {Array.isArray(matchingRules) && matchingRules.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowMatches((s) => !s)}
            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showMatches
              ? t("hideAllMatches")
              : `${t("showAllMatches")} (${matchingRules.length})`}
          </button>
          {showMatches && (
            <div className="mt-2 space-y-2">
              {matchingRules.map((rule, i) => {
                const rid = pick(rule, "ruleId", "RuleId") || `rule-${i}`;
                return (
                  <div
                    key={rid}
                    className="p-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800 dark:text-gray-100">
                        {pick(rule, "recommendedExam", "RecommendedExam") ||
                          "—"}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {rid}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mt-0.5">
                      {pick(rule, "acrVariant", "AcrVariant") || ""}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400">
                      {pick(rule, "recommendedExamRating", "RecommendedExamRating") ||
                        ""}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Extracted text */}
      {extractedText && (
        <div>
          <button
            type="button"
            onClick={() => setShowExtracted((s) => !s)}
            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showExtracted ? t("hideExtractedText") : t("showExtractedText")}
          </button>
          {showExtracted && (
            <textarea
              readOnly
              value={extractedText}
              className="mt-1 w-full h-40 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-2 text-xs font-mono"
            />
          )}
        </div>
      )}
    </div>
  );
}

function RuleField({ label, value, hint, compact, wide }) {
  if (!value && !hint) return null;
  const display = String(value ?? "—");
  return (
    <div
      className={`${
        wide ? "sm:col-span-2" : ""
      } flex flex-col ${compact ? "items-end text-right" : ""}`}
    >
      <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <span className="text-sm text-gray-800 dark:text-gray-100">
        {display}
        {hint ? (
          <span className="ml-1 text-[10px] text-gray-500 dark:text-gray-400">
            ({hint})
          </span>
        ) : null}
      </span>
    </div>
  );
}
